import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { NeptuneApi } from '../genericApi/neptune.api';
import { CoverageInsightsApi } from './model/coverage-insights-api.model';
import { KeyMessageGroupApi } from './model/key-message-group-api.';
import { KeyMessageApi } from './model/key-message-api.';
import { CanvasAgentResponseApiModel } from './model/canvas-agent-response-api.model';
import { CustomInsightApi } from './model/custom-insights-api.model';
import { CanvasV2AcceptedModel } from './model/new-canvas-agent-response-api.model';

@Injectable()
export class AIApi {

    constructor(
        private neptuneApi: NeptuneApi
    ) {}

    /* eslint-disable @typescript-eslint/naming-convention */
    postCompletions(prompt: any): Observable<any> {
        const headers = new HttpHeaders({'accept': 'text/plain', 'content-type': 'application/json'});

        return this.neptuneApi.streamApi(
            '/ai/chat/completions',
            prompt,
            HttpVerbsEnum.POST,
            {},
            'text',
            headers
        );
    }

    analysis(payload: CoverageInsightsApi): Observable<any> {
        return this.neptuneApi.api<any>(
            '/mentionai/bulk/analysis',
            payload,
            HttpVerbsEnum.POST
        );
    }

    getKeyMessages(payload: CoverageInsightsApi): Observable<KeyMessageGroupApi[]> {
        return this.neptuneApi.api<any>(
            '/mentionai/bulk/keymessages',
            payload,
            HttpVerbsEnum.POST
        );
    }

    getUserDefinedKeyMessages(payload: CoverageInsightsApi): Observable<KeyMessageApi[]> {
        return this.neptuneApi.api<any>(
            '/mentionai/bulk/userdefinedkeymessages',
            payload,
            HttpVerbsEnum.POST
        );
    }

    getCustomAnalysis(): Observable<CustomInsightApi[]> {
        return this.neptuneApi.api(
            '/agents/custom-prompts',
            HttpVerbsEnum.GET
        );
    }

    /**
     * Resolves to the new prompt's id. agents-service answers with `{ id }` rather than the bare
     * number the callers bind to, so the id is unwrapped here rather than in each of them.
     */
    createCustomAnalysis(payload: any): Observable<number> {
        return this.neptuneApi.api<number | { id: number }>(
            '/agents/custom-prompts',
            payload,
            HttpVerbsEnum.POST
        ).pipe(
            map((response: number | { id: number }) => typeof response === 'number' ? response : response?.id)
        );
    }

    updateCustomAnalysis(payload: any): Observable<any> {
        return this.neptuneApi.api<any>(
            `/agents/custom-prompts/${payload.id}`,
            payload,
            HttpVerbsEnum.PUT
        );
    }

    deleteCustomAnalysis(id: number): Observable<any> {
        return this.neptuneApi.api<any>(
            `/agents/custom-prompts/${id}`,
            null,
            HttpVerbsEnum.DELETE
        );
    }

    canvasAgent(prompt: string): Observable<CanvasAgentResponseApiModel> {
        return this.neptuneApi.api<CanvasAgentResponseApiModel>(
            '/agents/canvas',
            { userMessage: prompt },
            HttpVerbsEnum.POST
        );
    }

    canvasAgentV2(prompt: string): Observable<CanvasV2AcceptedModel> {
        return this.neptuneApi.api<CanvasV2AcceptedModel>(
            '/agents/canvas-v2',
            { userMessage: prompt },
            HttpVerbsEnum.POST
        );
    }

    runWorkflow(payload: any): Observable<any> {
        return this.neptuneApi.api<any>(
            '/workflow/run',
            payload,
            HttpVerbsEnum.POST
        );
    }

    resolveRedirects(urls: string[]): Observable<string[]> {
        return this.neptuneApi.api<string[]>(
            '/proxy/redirects',
            { urls },
            HttpVerbsEnum.POST
        );
    }
}
